import { Injectable } from '@angular/core';
import {
  EXCEL_FILE_STANDARD_DATA,
  TIME_HORIZON_SCENARIO_ROW_NAME,
} from '../../constants';
import {
  CarePathwayRow,
  KeyValueRow,
  ResultRow,
  ScenarioRow,
  SummaryRow,
} from '../structured-input/structured-input.service';
import { COMPLIANCE_CHECKER_ERRORS } from './compliance-checker-errors';

@Injectable({
  providedIn: 'root',
})
export class ComplianceCheckerService {
  checkParsedFileCompliance(
    resultRows: ResultRow[],
    scenarioRows: ScenarioRow[],
    carePathwayRows: CarePathwayRow[],
    introRows: KeyValueRow[],
    configRows: KeyValueRow[],
    summaryRows: SummaryRow[]
  ): void {
    if (scenarioRows.length == 0) {
      this._throwError(
        EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
        COMPLIANCE_CHECKER_ERRORS.SHEET_MISSING_OR_EMPTY
      );
    }
    if (introRows.length == 0) {
      this._throwError(
        EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.INTRO,
        COMPLIANCE_CHECKER_ERRORS.SHEET_MISSING_OR_EMPTY
      );
    }
    if (configRows.length == 0) {
      this._throwError(
        EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CONFIG,
        COMPLIANCE_CHECKER_ERRORS.SHEET_MISSING_OR_EMPTY
      );
    }

    this._checkResultSheet(resultRows);
    this._checkScenarioSheet(scenarioRows);
    this._checkCarePathwaySheet(carePathwayRows);
    this._checkIntroSheet(introRows);
    this._checkConfigSheet(configRows);
    this._checkSummaryRows(summaryRows);
  }

  private _checkResultSheet(rows: ResultRow[]): void {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (i === 0 && !row.categoryName) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.RESULTS,
          COMPLIANCE_CHECKER_ERRORS.Result.FIRST_ROW_NOT_CATEGORY,
          i
        );
      }

      if (row.categoryName && !row.subcatName) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.RESULTS,
          COMPLIANCE_CHECKER_ERRORS.Result.CATEGORY_WITHOUT_SUBCATEGORY,
          i
        );
      }

      if (!row.subcatValueName) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.RESULTS,
          COMPLIANCE_CHECKER_ERRORS.Result.VALUE_MISSING,
          i
        );
      }

      if (
        row.subcatValueHierarchy == null ||
        row.subcatValueY1 == null ||
        row.subcatValueY2 == null ||
        row.subcatValueY3 == null ||
        row.subcatValueY4 == null ||
        row.subcatValueY4 == null
      ) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.RESULTS,
          COMPLIANCE_CHECKER_ERRORS.Result.INCOMPLETE_VALUE,
          i
        );
      }

      if (parseInt(row.subcatValueHierarchy!) < 0) {
        // safe to force ! thanks to previous check
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.RESULTS,
          COMPLIANCE_CHECKER_ERRORS.Result.INVALID_HIERARCHY,
          i
        );
      }
    }
  }

  private _checkScenarioSheet(rows: ScenarioRow[]): void {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (!row.name || !row.defaultValue) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
          COMPLIANCE_CHECKER_ERRORS.Scenario.MISSING_NAME_OR_DEFAULT,
          i
        );
      }

      if (row.options) {
        if (row.options.length < 2) {
          this._throwError(
            EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
            COMPLIANCE_CHECKER_ERRORS.Scenario.INVALID_OPTIONS_FORMAT,
            i
          );
        }

        if (!row.options.includes(row.defaultValue!.toString())) {
          this._throwError(
            EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
            COMPLIANCE_CHECKER_ERRORS.Scenario.DEFAULT_NOT_IN_OPTIONS,
            i
          );
        }
      }

      if (i === 0 && !row.category) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
          COMPLIANCE_CHECKER_ERRORS.Scenario.FIRST_ROW_MISSING_CATEGORY,
          i
        );
      }
    }

    // if (
    //   rows.filter((row) => row.name === TIME_HORIZON_SCENARIO_ROW_NAME)
    //     .length !== 1
    // ) {
    //   this._throwError(
    //     EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
    //     COMPLIANCE_CHECKER_ERRORS.Scenario.TIME_HORIZON_ROW_MISSING
    //   );
    // }
  }

  private _checkCarePathwaySheet(rows: CarePathwayRow[]): void {
    if (rows.length == 0) return;

    let stepAmount = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (i === 0 && !row.stepTitle) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY,
          COMPLIANCE_CHECKER_ERRORS.CarePathway.FIRST_ROW_NOT_STEP,
          i
        );
      }

      if (
        (row.stepTitle && (!row.stepMatIconName || !row.impactCategoryName)) ||
        (row.stepMatIconName && (!row.stepTitle || !row.impactCategoryName))
      ) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY,
          COMPLIANCE_CHECKER_ERRORS.CarePathway.INCOMPLETE_STEP,
          i
        );
      }

      // safe since already verified step integrity
      if (row.stepTitle) {
        stepAmount++;
      }

      if (row.impactCategoryName && !row.impactName) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY,
          COMPLIANCE_CHECKER_ERRORS.CarePathway.INCOMPLETE_IMPACT_CATEGORY,
          i
        );
      }

      if (
        !(
          (row.impactName && row.impactCardIndex && row.subImpact1Name) ||
          (!row.impactName && !row.impactCardIndex && !row.subImpact1Name)
        )
      ) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY,
          COMPLIANCE_CHECKER_ERRORS.CarePathway.INCOMPLETE_IMPACT,
          i
        );
      }

      if (
        (row.subImpact1Name && !row.subImpact1Value) ||
        (!row.subImpact1Name && row.subImpact1Value)
      ) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY,
          COMPLIANCE_CHECKER_ERRORS.CarePathway.INCOMPLETE_SUBIMPACT_1,
          i
        );
      }

      if (
        (row.subImpact2Name && !row.subImpact2Value) ||
        (!row.subImpact2Name && row.subImpact2Value) ||
        (row.subImpact2Name && !row.subImpact1Name)
        // safe to only check name since both are already integrity-checked
        // last check required to avoid impacts containing subImpact2 but not subImpact1
      ) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY,
          COMPLIANCE_CHECKER_ERRORS.CarePathway.INCOMPLETE_SUBIMPACT_2,
          i
        );
      }
    }

    if (stepAmount !== 0 && (stepAmount < 3 || stepAmount > 6)) {
      this._throwError(
        EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY,
        COMPLIANCE_CHECKER_ERRORS.CarePathway.STEP_AMOUNT_OUT_OF_RANGE +
          stepAmount
      );
    }
  }

  private _checkIntroSheet(rows: KeyValueRow[]) {
    this._checkKeyValueSheet(rows, EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.INTRO);
  }

  private _checkConfigSheet(rows: KeyValueRow[]) {
    this._checkKeyValueSheet(rows, EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CONFIG);
  }

  private _checkKeyValueSheet(rows: KeyValueRow[], sheetName: string) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (!row.key || !row.value) {
        this._throwError(
          sheetName,
          COMPLIANCE_CHECKER_ERRORS.KeyValueSheets.MISSING_KEY_OR_VALUE,
          i
        );
      }
    }
  }

  private _checkSummaryRows(rows: SummaryRow[]) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (
        !(
          (row.title && row.matIconName && row.htmlContent) ||
          (!row.title && !row.matIconName && !row.htmlContent)
        )
      ) {
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SUMMARY,
          COMPLIANCE_CHECKER_ERRORS.Summary.INVALID_ITEM,
          i
        );
      }

      if (row.imageFileName && row.title) {
        // check only via title because entity integrity checked prior
        this._throwError(
          EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
          COMPLIANCE_CHECKER_ERRORS.Summary.INVALID_IMAGE,
          i
        );
      }
    }
  }

  private _throwError(sheet: string, message: string, rowIndex?: number): void {
    const fullMessage =
      rowIndex == 0 || rowIndex
        ? `[Feuille '${sheet}', Ligne ${rowIndex + 2}] ${message}`
        : `[Feuille '${sheet}'] : ${message}`;
    throw new Error(fullMessage);
  }
}
